// モフトレチェック・詳細.
import * as React from 'react';
import * as Header from '../constants/Header';
import { calcAge, getFirstEndDay, getYearMonthArray, isValidDate } from '../utils/dateUtil';
import '../css/moff_check.css';
import * as Enumerable from 'linq';
import { User, Male, Female } from '../constants/UserListField';
import { TUGTableSeconds, TUGTable, BalanceTable, Institution } from '../constants/MoffCheck';
import { Decimal } from 'decimal.js';
import * as TrainingConst from '../constants/Training';
import { MOFF_CHECK_DATA } from '../common/data/moffcheck_data';
import MoffCheckModule from '../utils/MoffCheckModule';

// 判定に使う間隔の定数.
const TUG_SECONDS: number[] = [6, 8, 10, 11, 13, 15, 20, 30];
const ONE_LEG_SECONDS: number[] = [0, 5, 15, 20, 30, 40, 50, 60];

// データがない.
const NO_DATA: number = -1;

interface MoffCheckProps extends React.Props<{}> {
  moffAPI: any;
  moffCheck?: any;
  moffAPIActions: any;
  headerActions?: any;

  moffCheckActions?: any;
  // 前ページのユーザーID.
  match?: any;
}

interface MoffCheckState {
}

/**
 * モフトレチェックのクラス.
 *
 * @export
 * @class MoffCheck
 * @extends {React.Component<MoffCheckProps, MoffCheckState>}
 */
export default class MoffCheck extends React.Component<MoffCheckProps, MoffCheckState> {
  private months: string[] = [];
  private tugTimes: number[] = [];
  private tugData: {[index: string]: TUGTableSeconds; } = {};
  private oneLegTimes: number[] = [];

  // 施設名.
  private facilityName: string;

  /**
   * render()直前の処理.
   *
   * @memberof MoffCheck
   */
  componentWillMount() {
    document.title = 'モフトレチェック';

    this.props.headerActions.setPage(Header.PRESS);

    // URLのパラメータ.
    const userId = this.props.match.params.userId;

    const yearMonth: string = this.props.match.params.yearMonth;

    // 値が"YYYY-MM"かをチェック.
    const regex = /\d{4}-\d{2}/;
    if (yearMonth.match(regex) === null) {
      return;
    }

    // 月確認.
    const month: number = Number(yearMonth.split('-')[1]);
    if (month < 0 || month > 12) {
      return;
    }

    // API実行.
    this.runAPI(userId, yearMonth);
  }

  /**
   * img以下のパスから画像取得.
   *
   * @param <number> month (required) img以下のパス.
   * @return <string>
   */
  getImage(imagePath: string): string {
    const image: string = require(`../img/${imagePath}`);
    return image;
  }

  /**
   * 位置を決める.
   */
  calcPosition(): any[][][] {
    // 縦横インデックスと、対応の月.
    let data: any[][][] = [
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
      [[], [], [], [], [], [], []],
    ];

    // TUGの値.
    const tug = this.tugTimes;

    // 開眼片足立ちの値.
    const oneLeg = this.oneLegTimes;
    // TUG
    let tugCopy = TUG_SECONDS.concat(); // 値コピー.
    // 先頭消す.
    tugCopy.shift();
    // 逆順.
    let oneLegSecondsRev = ONE_LEG_SECONDS.concat(); // 値コピー.
    oneLegSecondsRev.reverse();
    // 先頭消す.
    oneLegSecondsRev.shift();

    // すでに見つけたもの.
    let skipMonths: string[] = [];
    for (let i = 0; i < tugCopy.length; i++) {
      for (let j = 0; j < oneLegSecondsRev.length; j++) {
        if (skipMonths.length >= this.months.length) {
          break;
        }

        this.months.map((month: string, index: number) => {
          if (tug[index] === NO_DATA || oneLeg[index] === NO_DATA) {
            // 片方でもデータがないのでスキップ.
            return;
          }

          // TUGが最大値の右側を超えない策.
          let isRightMax = false;
          if ((i === tugCopy.length - 1) && (tug[index] >= tugCopy[tugCopy.length - 1])) {
            isRightMax = true;
          }

          if ((tug[index] < tugCopy[i] || isRightMax) && oneLeg[index] >= oneLegSecondsRev[j]) {
            // すでに見つけたのはスキップ.
            if (skipMonths.indexOf(month) >= 0) {
              return;
            }

            // monthは「"2018.07"」なので月の数字を取得する.
            let monthNum = Number(month.split('.')[1]);
            data[j][i].push(monthNum);
            // スキップ確認用.
            skipMonths.push(month);
          }
        });
      }
    }

    return data;
  }

  /**
   * 時間の差分(+や-や±も踏まえて表示).
   *
   * @param  <number> month (required) 最初の月の秒数.
   * @param  <number> lastMonth (required) 最後の月の秒数.
   * @return <string>
   */
  diffSecond(month: number, lastMonth: number): string {
    if (month === NO_DATA || lastMonth === NO_DATA) {
      return '';
    }

    const x = new Decimal(month);
    const y = x.minus(lastMonth);

    const comp = x.comparedTo(lastMonth);
    let article: string = '';

    // -はそのまま表示されるため後付けしない.
    if (comp === 0) {
      article = '±';
    } else if (comp > 0) {
      article = '+';
    }

    return `(${article}${y.toString()})`;
  }

  /**
   * 上下の矢印.
   *
   * @param  <boolean> isTUG (required) TUGかどうか.
   * @param  <number> month (required) 最初の月の秒数(データがないときは-1).
   * @param  <number> lastMonth (required) 最後の月の秒数(データがないときは-1).
   * @returns {(JSX.Element | null)}
   * @memberof MoffCheck
   */
  checkArrow(isTUG: boolean, month: number, lastMonth: number): JSX.Element | null {
    // 片方データなし.
    if (month === NO_DATA || lastMonth === NO_DATA) {
      return (null);
    }

    const x = new Decimal(month);
    const comp = x.comparedTo(lastMonth);
    let arrow = '';
    if (isTUG) {
      if (comp > 0) {
        arrow = 'icon_down';
      } else if (comp === 0) {
        arrow = 'icon_keep';
      } else {
        // TUGの場合は小さくなると上.
        arrow = 'icon_up';
      }
    } else {
      if (comp > 0) {
        arrow = 'icon_up';
      } else if (comp === 0) {
        arrow = 'icon_keep';
      } else {
        arrow = 'icon_down';
      }
    }

    const imgSrc: string = this.getImage(`${arrow}.png`);
    return <img src={imgSrc} className="arrow_icon" />;
  }

  /**
   * 範囲内でAPIを叩く.
   *
   * @param {string} userId
   * @param {string} yearMonth
   * @memberof MoffCheck
   */
  runAPI(userId: string, yearMonth: string) {
    const split = yearMonth.split('-');
    const yearSelected = split[0];
    const monthSelected = split[1];
    // 最初の日と最後の日を返す(3ヶ月分).
    const [startDate, endDate] = getFirstEndDay(yearSelected, monthSelected, 3);
    
    // API呼び出し.
    this.props.moffCheckActions.getMoffCheckData(userId, startDate, endDate);

    // 利用者情報と施設名.
    this.props.moffAPIActions.getMoffCheckDetailData(userId);
  }

  /**
   * CSSの描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderCSS(): JSX.Element {
    return (
      <style key="moffcheckCSS"  type="text/css">
        {/* 回数,トレーニング項目,カレンダーの行間 */}
        {'body { line-height: 1;background: none;}'}
        {/* 余白消す. */}
        {'#wrapper { padding: 0;border: 0;margin: 0;min-height: 0;}'}
        {'#wrapper .contentIn { padding: 0;border: 0;margin: 0;}'}
        {/* 縦印刷, 日付・title名ヘッダの余白 */}
        {'@media print{@page {size: 210mm 297mm;margin-top: 20px;}}'}
        {'@media print{#wrapper { margin-top: 0;} header { display: none;} '}
        {'.blue { background-color: blue;}'}

        {` body { -webkit-print-color-adjust: exact; ` +
         `font-family: 'Hiragino Kaku Gothic ProN', 'ヒラギノ角ゴ ProN W3', sans-serif; }`}
      </style>
    );
  }

  /**
   * 年齢取得.
   *
   * @returns
   * @memberof MoffCheck
   */
  getAge() {
    const user: User = this.props.moffAPI.result.user;

    // 誕生日のデータ.
    const year = Number(user.user_birth_year);
    const month = Number(user.user_birth_month);
    const date = Number(user.user_birth_day);
    const age = calcAge(year, month, date);
    return age;
  }

  /**
   * ローディング用レンダリング.
   *
   * @returns
   * @memberof MoffCheck
   */
  loadingRender() {
    return (
    <div>
      <div id="loader" /><div id="loader_back" />
      <style key="moffcheckCSS"  type="text/css">
      {'body { background: none;}'}
      </style>
    </div>);
  }

  /**
   * 描画.
   *
   * @returns
   * @memberof MoffCheck
   */
  render() {
    return this.props.moffAPI.Loaded ?
      // ロード後.
      (
        <div key="moffcheckTop" className="moffcheck">
        {
          this.props.moffCheck.Loaded ? this.renderBody() : this.loadingRender()
        }
        </div>
      )
      :
      // ロード中もCSSの背景が出ないように使用する.
      (
        <div key="moffcheckTop" className="moffcheck">
        {
          this.loadingRender()
        }
        </div>
      );
  }

  /**
   * 更新後.
   *
   * @memberof MoffCheck
   */
  componentDidUpdate() {
    // 更新後.
  }

  /**
   * 男性、女性表示.
   *
   * @param {number} gender
   * @returns {string}
   * @memberof MoffCheck
   */
  getGender(gender: number): string {
    return `${gender === 0 ? Male : Female}性`;
  }

  /**
   * ロゴの描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderLogo(): JSX.Element {
    const imgSrc = this.getImage('logo.png');
    return (
      <img key="moffcheck1_1" src={imgSrc} className="moffcheck_logo" alt="モフトレ" />
    );
  }

  /**
   * タイトルの描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderTitle(): JSX.Element {
    // 施設名.
    const institution: Institution = this.props.moffAPI.result.institutions.institution;

    this.facilityName = institution.institution_name;
    return (
      <p key="moffcheck1_2" className="moffcheck_name">{this.facilityName}</p>
    );
  }

  /**
   * 利用者の上の画像描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderUserTitle(): JSX.Element {
    const imgSrc = this.getImage('head_user.png');
    return (
      <img key="moffcheck2_1" src={imgSrc} />
    );
  }

  /**
   * 誕生日表示用文字列生成.
   *
   * @param {User} user
   * @returns {string}
   * @memberof MoffCheck
   */
  makeBirthday(user: User): string {
    const year = user.user_birth_year;
    const month = user.user_birth_month;
    const date = user.user_birth_day;

    // 数字ではないデータが入っているのを防止.
    if (!isValidDate(year, month, date)) {
      return `${year}.　.　生`;
    }

    return `${year}.${month}.${date}生`;
  }

  /**
   * 利用者の情報のJSXを描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderUserInfo(): JSX.Element {
    const user: User = this.props.moffAPI.result.user;

    return (
      <table key="moffcheck2_2" className="moffcheck_user">
        <tbody>
        <tr className="moffcheck_user__inner">
          <td className="table_left user_name">{user.user_name}</td>
          <td className="user_sex">{this.getGender(user.user_gender)}</td>
          <td className="user_birthday">{this.makeBirthday(user)}</td>
          <td className="user_old">{this.getAge()}歳</td>
          <td>{TrainingConst.CARE_LEVELS[user.user_care_level]}</td>
        </tr>
        </tbody>
      </table>
    );
  }

  /**
   * スコアの上の画像描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderScoreTitle(): JSX.Element {
    const imgSrc = this.getImage('head_score.png');
    return (
      <img key="moffcheck3_1" src={imgSrc} />
    );
  }

  /**
   * 平均の配列.
   *
   * @param {number} gender
   * @param {number} age
   * @returns {string[]}
   * @memberof MoffCheck
   */
  calcAverage(gender: number, age: number): string[] {
    let index: number = NO_DATA;
    for (let i: number = 0; i < MOFF_CHECK_DATA.age.length; i++) {
      // 以上-以下の順でということに注意.
      const min: number = parseInt(MOFF_CHECK_DATA.age[i].split('〜')[0], 10);
      const maxStr: string = MOFF_CHECK_DATA.age[i].split('〜')[1];

      // 最後のときはないので.
      if (maxStr === '') {
        if (min <= age) {
          index = i;
          break;
        }
      }

      const max: number = parseInt(maxStr, 10);

      if (min <= age && max >= age) {
          index = i;
          break;
      }
    }

    // 64歳以下は全部64歳以下の平均値を出すため最小の範囲の0index.
    if (index === NO_DATA) {
      index = 0;
    }

    let ageMsg = '歳平均';

    // 歳範囲のメッセージ(85歳超えたとき対応).
    let ageMessage = (MOFF_CHECK_DATA.age[index] + ageMsg).replace('〜歳平均', '歳以上平均');
    if (index === 0) {
      ageMessage = ageMessage.replace('歳平均', '歳以下平均')
        .replace(/[0-9]*〜/g, '');
    }

    // 男.
    if (gender === 0) {
      return [
        ageMessage,
        this.makeTUGAverageMessage(MOFF_CHECK_DATA.tug.male, index),
        this.makeBalanceAverageMessage(MOFF_CHECK_DATA.balance.male, index),
      ];
    }

    // 女.
    return [
      ageMessage,
      this.makeTUGAverageMessage(MOFF_CHECK_DATA.tug.female, index),
      this.makeBalanceAverageMessage(MOFF_CHECK_DATA.balance.female, index),
    ];
  }

  /**
   * TUGの平均値のマスタから「最小〜最大平均値」の形式にて表示する.
   *
   * @param {{[key: string]: number}[]} arr
   * @param {number} index
   * @returns {string}
   * @memberof MoffCheck
   */
  makeTUGAverageMessage(arr: {[key: string]: number}[], index: number): string {
    let min: number = arr[index].min;
    let max: number = arr[index].max;

    // 文字列に変換(numberなので文字列変換時少数第2位の0が消えないようにtoFixed).
    return `${min.toFixed(2)}〜${max.toFixed(2)}`;
  }

  /**
   * 開眼片足立ち立位の平均値のマスタから「最小〜最大平均値」の形式にて表示する.
   *
   * @param {{[key: string]: number}[]} arr
   * @param {number} index
   * @returns {string}
   * @memberof MoffCheck
   */
  makeBalanceAverageMessage(arr: {[key: string]: number}[], index: number): string {
    let min: number = arr[index].min;
    let max: number = arr[index].max;

    // TUGと異なり秒数は小数点以下はない.
    return `${min}〜${max}`;
  }

  /**
   * スコアの表のJSXを描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderScoreTable(): JSX.Element {
    const user: User = this.props.moffAPI.result.user;
    // TUGの値.
    const term1TUG = this.tugTimes[0];
    const term2TUG = this.tugTimes[1];
    const term3TUG = this.tugTimes[2];

    // 開眼片足立ちの値.
    const term1OneLeg = this.oneLegTimes[0];
    const term2OneLeg = this.oneLegTimes[1];
    const term3OneLeg = this.oneLegTimes[2];

    // 平均.
    const gender: number = this.props.moffAPI.result.user.user_gender;
    const age: number = this.getAge();
    const average = this.calcAverage(gender, age);

    return (
      <table key="moffcheck3_2" className="moffcheck_score">
        <tbody>
        <tr className="moffcheck_score__inner">
          {/* タイトル */}
          {
            // 月部分(3ヶ月分).
            Enumerable.range(0, 4)
              .select<JSX.Element>(
                (i) => {
                  if (i === 0) {
                    // 最初は空欄.
                    return <td key={`best_${i}`} className="table_left moffcheck_score__col">&nbsp;</td>;
                  }
                  return <td key={`best_${i}`}>{this.months[i - 1]}</td>;
                }
              ).toArray()
          }
          <td>{average[0]}</td>
        </tr>
        <tr className="moffcheck_score__inner">
          <td className="table_left moffcheck_score__col">TUG</td>
          <td>
            {this.getSecond(term1TUG)} {this.diffSecond(term1TUG, term2TUG)}
            {this.checkArrow(true, term1TUG, term2TUG)}
          </td>
          <td>{this.getSecond(term2TUG)}</td>
          <td>{this.getSecond(term3TUG)}</td>
          <td>{this.getGender(user.user_gender)} {average[1]}秒</td>
        </tr>
        <tr className="moffcheck_score__inner">
          <td className="table_left moffcheck_score__col">開眼片足立ち立位</td>
          <td>
            {this.getSecond(term1OneLeg)} {this.diffSecond(term1OneLeg, term2OneLeg)}
            {this.checkArrow(false, term1OneLeg, term2OneLeg)}
          </td>
          <td>{this.getSecond(term2OneLeg)}</td>
          <td>{this.getSecond(term3OneLeg)}</td>
          <td>{this.getGender(user.user_gender)} {average[2]}秒</td>
        </tr>
        <tr className="moffcheck_score__inner">
          <td className="table_left moffcheck_score__col">歩行能力評価</td>
          <td>{this.evaluateWalkingAbility(term1TUG, term1OneLeg)}</td>
          <td>{this.evaluateWalkingAbility(term2TUG, term2OneLeg)}</td>
          <td>{this.evaluateWalkingAbility(term3TUG, term3OneLeg)}</td>
          <td>{/* 平均は評価不要 */}</td>
        </tr>
        </tbody>
      </table>
    );
  }

  /**
   * 時間秒付きで返す.
   *
   * @param {number} time
   * @returns {string}
   * @memberof MoffCheck
   */
  getSecond(time: number): string {
    if (time === NO_DATA) {
      return '-';
    }
    return `${time}秒`;
  }

  /**
   * 歩行能力評価の判定を行う.
   *
   * @param {number} tugTime TUGの時間.
   * @param {number} legTime 開眼片足立ち立位の時間.
   * @returns {string}
   * @memberof MoffCheck
   */
  evaluateWalkingAbility(tugTime: number, legTime: number): string {
    if (tugTime === NO_DATA || legTime === NO_DATA) {
      // ないときはない.
      return '-';
    }

    if (tugTime < TUG_SECONDS[1]) {
      if (legTime >= ONE_LEG_SECONDS[4]) {
        return 'S';
      } else if (legTime >= ONE_LEG_SECONDS[3]) {
        return 'A';
      } else if (legTime >= ONE_LEG_SECONDS[2]) {
        return 'B';
      } else if (legTime >= ONE_LEG_SECONDS[1]) {
        return 'C';
      }

      return 'D';
    } else if (tugTime < TUG_SECONDS[2]) {
      if (legTime >= ONE_LEG_SECONDS[3]) {
        return 'A';
      } else if (legTime >= ONE_LEG_SECONDS[2]) {
        return 'B';
      } else if (legTime >= ONE_LEG_SECONDS[1]) {
        return 'C';
      }

      return 'D';
    } else if (tugTime < TUG_SECONDS[3]) {
      if (legTime >= ONE_LEG_SECONDS[2]) {
        return 'B';
      } else if (legTime >= ONE_LEG_SECONDS[1]) {
        return 'C';
      }

      return 'D';
    } else if (tugTime < TUG_SECONDS[4]) {
      if  (legTime >= ONE_LEG_SECONDS[1]) {
        return 'C';
      }

      return 'E';
    } else {
      if (legTime >= ONE_LEG_SECONDS[2]) {
        return 'D';
      }

      return 'E';
    }
  }

  /**
   * TUGの上の画像描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderTUGTitle(): JSX.Element {
    const imgSrc = this.getImage('head_tug.png');
    return (
      <img key="moffcheck4_1" src={imgSrc} />
    );
  }

  /**
   * TUG周期別の秒数の秒のJSXを描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderTUGTable(): JSX.Element {
    const first = this.months[0];
    const firstData = this.tugData[first];
    const second = this.months[1];
    const secondData = this.tugData[second];
    const third = this.months[2];
    const thirdData = this.tugData[third];

    const arr = [
      [first,
        firstData ? firstData.stand_up : null, firstData ? firstData.out : null, firstData ? firstData.out_turn : null,
        firstData ? firstData.in : null, firstData ? firstData.in_turn : null, firstData ? firstData.sit : null
      ],
      [second,
        secondData ? secondData.stand_up : null, secondData ? secondData.out : null,
        secondData ? secondData.out_turn : null,
        secondData ? secondData.in : null, secondData ? secondData.in_turn : null, secondData ? secondData.sit : null
      ],
      [third,
        thirdData ? thirdData.stand_up : null, thirdData ? thirdData.out : null, thirdData ? thirdData.out_turn : null,
        thirdData ? thirdData.in : null, thirdData ? thirdData.in_turn : null, thirdData ? thirdData.sit : null
      ],
    ];

    return (
      <table key="moffcheck4_2" className="moffcheck_tug">
        <tbody>
          {/* タイトル */}
          {
            // 1st to 8th.
            Enumerable.from(arr)
              .select<JSX.Element>(
                (row, num) => {
                  let rows = [];
                  for (let i = 0; i < row.length; i++) {
                    let item: string = '-';
                    if (row[i] !== null) {
                      item = `${row[i]}`;
                    }

                    // 一番左の年月.
                    if (i === 0) {
                      item = item.replace('/', '.');
                      rows.push(<td key={`item_${num}_${i}`} className="table_left">{item}</td>);
                    } else {
                      // 時間.
                      rows.push(<td key={`item_${num}_${i}`} className="table_right">{item}</td>);
                    }
                  }

                  return <tr key={`tr_${num}`} className="moffcheck_tug__inner">{rows}</tr>;
                }
              ).toArray() 
          }
        </tbody>
      </table>
    );
  }

  /**
   * 背景の画像を取得.
   *
   * @param {string} imagePath
   * @returns {React.CSSProperties}
   * @memberof MoffCheck
   */
  getBackgroundImage(imagePath: string): React.CSSProperties {
    const style = {
      backgroundImage: `url(${this.getImage(`cell_bg_${imagePath}.png`)})`
    };
    return style;
  }

  /**
   * グラフの上の画像描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  rendeGraphTitle(): JSX.Element {
    const imgSrc = this.getImage('graph_head.png');
    return (
      <img key="moffcheck5_1" src={imgSrc} />
    );
  }

  /**
   * 月の画像取得.
   *
   * @param {number} monthNumber
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  getMonthImage(monthNumber: number): JSX.Element {
    const engName = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const imgSrc = this.getImage(`pin_${engName[monthNumber - 1]}.png`);
    return (
      <span key={`month_${monthNumber}`}><img src={imgSrc} className="moffcheck_graph__pin" />&nbsp;</span>
    );
  }

  /**
   * グラフのJSXを描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  rendeGraphTable(): JSX.Element {
    // 月のマーカーの設定処理.
    const arr: any = this.calcPosition();

    return (
      <div key="moffcheck5_2" className="moffcheck_graph">
        <div className="moffcheck_graph__l">
          <img src={this.getImage('graph_second.png')} />
        </div>
        <div className="moffcheck_graph__r">
          <img src={this.getImage('graph_space.png')} />
          <table className="moffcheck_graph__table">
          <tbody>
            <tr className="moffcheck_graph__table_head">
              <td>&nbsp;</td>
              <td>{TUG_SECONDS[0]}〜{TUG_SECONDS[1]}</td>
              <td>{TUG_SECONDS[1]}〜{TUG_SECONDS[2]}</td>
              <td>{TUG_SECONDS[2]}〜{TUG_SECONDS[3]}</td>
              <td>{TUG_SECONDS[3]}〜{TUG_SECONDS[4]}</td>
              <td>{TUG_SECONDS[4]}〜{TUG_SECONDS[5]}</td>
              <td>{TUG_SECONDS[5]}〜{TUG_SECONDS[6]}</td>
              <td>{TUG_SECONDS[6]}〜{TUG_SECONDS[7]}</td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[7]}〜{ONE_LEG_SECONDS[6]}</td>
              <td><img src={this.getImage('graph_body_blue.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_green.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[0][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[6]}〜{ONE_LEG_SECONDS[5]}</td>
              <td><img src={this.getImage('graph_body_blue.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_green.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td>
                <img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[1][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[5]}〜{ONE_LEG_SECONDS[4]}</td>
              <td><img src={this.getImage('graph_body_blue.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_green.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[2][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                  </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[4]}〜{ONE_LEG_SECONDS[3]}</td>
              <td><img src={this.getImage('graph_body_green.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_green.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][2].map((month: number) => {
                     return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[3][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[3]}〜{ONE_LEG_SECONDS[2]}</td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][0].map((month: number) => {
                     return this.getMonthImage(month);
                   })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_yellow.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][5].map((month: number) => {
                    return this.getMonthImage(month) ;
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[4][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[2]}〜{ONE_LEG_SECONDS[1]}</td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_purple.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                 </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[5][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
            <tr className="moffcheck_graph__table_body">
              <td>{ONE_LEG_SECONDS[1]}〜{ONE_LEG_SECONDS[0]}</td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][0].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][1].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_pink.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][2].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][3].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][4].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][5].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
              <td><img src={this.getImage('graph_body_red.png')} />
                <div className="moffcheck_graph__pin_wrap">
                  {arr[6][6].map((month: number) => {
                    return this.getMonthImage(month);
                  })}
                </div>
              </td>
            </tr>
          </tbody>
          </table>
        </div>
      </div>
    );
  }

  /**
   * グラフの下の画像描画.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  rendeGraphFooter(): JSX.Element {
    const imgSrc = this.getImage('graph_foot.png');
    return (
      <img key="moffcheck5_3" src={imgSrc} />
    );
  }

  /**
   * 本体のJSX.
   *
   * @returns {JSX.Element}
   * @memberof MoffCheck
   */
  renderBody(): JSX.Element[] {
    let result: any = this.props.moffCheck.result;
    let tugItems: TUGTable[] = result.tug.Items;
    let balanceItems: BalanceTable[] = result.balance.Items;

    let tugDic = MoffCheckModule.convertTUG(tugItems);
    let balanceDic = MoffCheckModule.convertBalance(balanceItems);

    // 3ヶ月分.
    const deltaMonths = 3;
    const yearMonth = this.props.match.params.yearMonth;
    const yearSelected = yearMonth.split('-')[0];
    const monthSelected = yearMonth.split('-')[1];
    let months = getYearMonthArray(yearSelected, monthSelected, deltaMonths);

    // すでに数が0のは選定済み.
    Enumerable.from(months)
    // 年月は逆順(2018-09 -> 2018-08 ->2018-07).
    .select<void>((element: string) => {
      // TUGの値.
      this.tugTimes.push(tugDic[element] ? tugDic[element].time : NO_DATA);

      // 開眼片足立ちの値.
      this.oneLegTimes.push(balanceDic[element] ? balanceDic[element].time : NO_DATA);

      // 2012.01
      const dot = element.replace('-', '.');
      this.months.push(dot);
      this.tugData[dot] = tugDic[element];
    }).toArray();

    return [
      this.renderLogo(),
      this.renderTitle(),
      this.renderUserTitle(),
      this.renderUserInfo(),
      this.renderScoreTitle(),
      this.renderScoreTable(),
      this.renderTUGTitle(),
      this.renderTUGTable(),
      this.rendeGraphTitle(),
      this.rendeGraphTable(),
      this.rendeGraphFooter(),
      // CSS.
      this.renderCSS()
    ];
  }
}



// WEBPACK FOOTER //
// ./src/components/MoffCheck.tsx